<template>
  <div class="lg:!mx-0 flex">
    <div class="flex flex-col max-h-content px-6 pb-2.5 min-h-content space-y-2.5 w-screen">
      <div class="mt-2.5">
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('HeadContract.CashFlow.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('HeadContract.CashFlow.subTitle') }}
            </p>
          </div>

          <div class="flex items-center space-x-4">
            <div class="bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
              <div class="text-sm text-gray-500">
                {{ $t('HeadContract.CashFlow.contractSum') }}
              </div>

              <div class="text-lg font-semibold text-gray-900">
                {{ $tcc(totalBudget) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col space-y-2 w-full overflow-y-auto flex-1">
        <div class="bg-white rounded-lg shadow-sm flex-1 flex flex-col">
          <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-1.5 border-b border-gray-200 rounded-t-lg">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-blue-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
</svg>
                </div>

                <h2 class="text-lg font-semibold text-gray-900">
                  {{ $t('HeadContract.CashFlow.overview') }}
                </h2>
              </div>

              <div class="flex items-center space-x-4">
                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-blue-500 rounded-full" />

                  <span class="text-sm text-gray-600">{{ $t('HeadContract.CashFlow.forecast') }}</span>
                </div>

                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-orange-500 rounded-full" />

                  <span class="text-sm text-gray-600">{{ $t('HeadContract.CashFlow.actual') }}</span>
                </div>

                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-red-500 rounded-full" />

                  <span class="text-sm text-gray-600">{{ $t('HeadContract.CashFlow.baseline') }}</span>
                </div>
              </div>
            </div>
          </div>

          <CashFlowLineChart
            v-if="project"
            :project="project"
            :forecast-cumulative="forecastCumulative"
            :monthly-certified-cumulative="monthlyCertifiedCumulative"
            :baseline-cumulative="baselineCumulative"
            :extended-chart-months="extendedChartMonths"
            @hover="handleLineChartHover"
            @mouseleave="handleLineChartHover(undefined)"
          />
        </div>

        <div class="w-full flex flex-1 text-sm 3xl:text-base space-x-2">
          <div class="w-2/3 rounded-lg flex flex-col relative bg-white">
            <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-1.5 border-b border-gray-200 rounded-t-lg">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-green-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                  </div>

                  <h3 class="text-lg font-semibold text-gray-900">
                    {{ $t('HeadContract.CashFlow.progressClaims') }}
                  </h3>
                </div>

                <Transition
                  mode="out-in"
                  enter-active-class="animate__animated animate__fadeIn animate__faster"
                  leave-active-class="animate__animated animate__fadeOut animate__faster"
                >
                  <p
                    v-if="claims.length > 0"
                    key="total-claims"
                    class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
                  >
                    <span class="font-medium text-gray-900">{{ claims.length }}&nbsp;</span>

                    <span class="text-gray-500">{{ $t('HeadContract.CashFlow.claim', claims.length) }}</span>
                  </p>
                </Transition>
              </div>
            </div>

            <div class="relative overflow-y-auto flex-1">
              <div class="absolute inset-0 overflow-y-auto">
                <table
                  v-if="project"
                  class="w-full"
                >
                  <thead class="bg-gray-50">
                    <tr class="child:p-2">
                      <th class="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {{ $t('HeadContract.CashFlow.claim') }}
                      </th>

                      <th class="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {{ $t('HeadContract.CashFlow.date') }}
                      </th>

                      <th class="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {{ $t('HeadContract.CashFlow.baselineMonthly') }}
                      </th>

                      <th class="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {{ $t('HeadContract.CashFlow.baselineCumulative') }}
                      </th>

                      <th class="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {{ $t('HeadContract.CashFlow.forecast') }}
                      </th>

                      <th class="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {{ $t('HeadContract.CashFlow.forecastCumulative') }}
                      </th>

                      <th class="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {{ $t('HeadContract.CashFlow.actualMonthly') }}
                      </th>

                      <th class="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {{ $t('HeadContract.CashFlow.actualCumulative') }}
                      </th>
                    </tr>
                  </thead>

                  <tbody class="bg-white divide-y divide-gray-100">
                    <tr
                      v-for="(claim, i) in claims"
                      :key="claim.id"
                      class="hover:bg-gray-50 transition-all duration-200 ease-in-out child:p-2"
                      :class="[isClaimDimmed(claimMonthToOffset(claim.startDate)) ? 'opacity-80' : hoveredIndex !== undefined ? 'bg-gray-100' : '']"
                    >
                      <td class="whitespace-nowrap">
                        <div class="flex items-center space-x-3">
                          <div class="flex-shrink-0">
                            <div class="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <span class="text-sm font-medium text-gray-900">{{ claim.claim_number }}</span>
                            </div>
                          </div>

                          <div class="flex items-center">
                            <span
                              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                              :class="[isClaimDimmed(claimMonthToOffset(claim.startDate)) ? 'bg-gray-100' : getStatusBadgeClass(claim.status)]"
                            >
                              <span
                                class="w-1.5 h-1.5 rounded-full mr-1.5"
                                :class="getStatusBadgeDotClass(claim.status)"
                              />
                              {{ $t(`Models.Claim.statusTitles.${enumKeyFromValue(ClaimStatus, claim.status)}`) }}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td class="whitespace-nowrap text-sm text-gray-900">
                        {{ claim.startDate?.toFormat('MMM yyyy') }}
                      </td>

                      <td class="whitespace-nowrap text-sm text-right text-gray-900">
                        {{ $tcc(baseline[claimMonthToOffset(claim.startDate)] ?? 0) }}
                      </td>

                      <td class="whitespace-nowrap text-sm text-right text-gray-900">
                        {{ $tcc(baselineCumulative[claimMonthToOffset(claim.startDate)] ?? baselineCumulative[baselineCumulative.length - 1]) }}
                      </td>

                      <td class="whitespace-nowrap text-sm text-right text-gray-900">
                        {{ $tcc(forecast[claimMonthToOffset(claim.startDate)] ?? 0) }}
                      </td>

                      <td class="whitespace-nowrap text-sm text-right text-gray-900">
                        {{ $tcc(forecastCumulative[claimMonthToOffset(claim.startDate)] ?? forecastCumulative[forecastCumulative.length - 1]) }}
                      </td>

                      <td class="whitespace-nowrap text-sm text-right text-gray-900">
                        {{ $tcc(claim.totalCertified) }}
                      </td>

                      <td class="whitespace-nowrap text-sm text-right text-gray-900">
                        {{ $tcc(claimCertifiedCumulative[i]) }}
                      </td>
                    </tr>

                    <tr v-if="claims.length === 0">
                      <td
                        colspan="8"
                        class="px-6 py-16 text-center"
                      >
                        <div class="flex flex-col items-center">
                          <div class="mx-auto h-12 w-12 text-gray-300 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12 text-gray-300">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                          </div>

                          <h3 class="text-sm font-medium text-gray-900 mb-1">
                            {{ $t('HeadContract.CashFlow.noClaimsFound') }}
                          </h3>

                          <p class="text-sm text-gray-500">
                            {{ $t('HeadContract.CashFlow.createFirstClaim') }}
                          </p>

                          <RouterLink
                            :to="{ name: 'ProgressClaimsIndex', params: { projectId: projectId } }"
                            class="btn btn--primary-blue mt-2"
                          >
                            {{ $t('HeadContract.CashFlow.button.createClaim') }}
                          </RouterLink>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="w-1/3 bg-white rounded-lg shadow-sm">
            <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-1.5 border-b border-gray-200 rounded-t-lg">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-purple-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
</svg>
                  </div>

                  <h3 class="text-lg font-semibold text-gray-900">
                    {{ $t('HeadContract.CashFlow.workComplete') }}
                  </h3>
                </div>
              </div>
            </div>

            <CashFlowPieChart
              :cumulative-amounts="monthlyCertifiedCumulative"
              :total-budget="totalBudget"
              :hovered-index="hoveredIndex"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CashFlowLineChart from '@/components/HeadContract/Cashflow/CashFlowLineChart.vue';
import CashFlowPieChart from '@/components/HeadContract/Cashflow/CashFlowPieChart.vue';
import useBoqLockRedirect from '@/composables/useBoqLockRedirect';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, cumulativeSum, enumKeyFromValue, roundDecimals } from '@/helpers';
import Claim, { ClaimStatus, ClaimStatusTitles } from '@/models/Claim';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { DateTime } from 'luxon';
import { computed, onMounted, ref, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

const projectsStore = useProjectsStore();
const claimsStore = useClaimsStore();
const tradesStore = useTradesStore();
const fetchClaimsAction = useStoreApiAction(claimsStore.fetchClaims);
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);

const project = computed(() => {
  return projectsStore.project;
});

const trades = computed(() => {
  return tradesStore.models
    .with('trade_items', (query) => {
      query.whereNull('parent_id');
    })
    .where('project_id', parseInt(props.projectId))
    .where('boq_type', 'direct')
    .orderBy('sort_order')
    .get();
});

const claims = computed(() => {
  return claimsStore.models.where('project_id', parseInt(props.projectId))
    .where('boq_type', 'direct')
    .orderBy(
      'claim_number',
      'asc',
    ).get();
});

watch(() => props.projectId, () => {
  fetchTradesAction.request(props.projectId, { boq_type: 'direct' }).catch((error) => {
    console.log(error);
  });

  fetchClaimsAction.request(props.projectId, { boq_type: 'direct' }).catch((error) => {
    console.log(error);
  });
}, { immediate: true });

useBoqLockRedirect();

const claimCertifiedCumulative = computed(() => {
  const cumulativeSum = ((sum) => (value: Claim) => sum += value.totalCertified)(0);

  return claims.value.map(cumulativeSum);
});

const monthlyCertifiedCumulative = computed(() => {
  if(!project.value) {
    return [];
  }

  const claimSumPerMonth = new Map<string, number>();
  const lastClaim = claims.value.at(-1);

  if(lastClaim) {
    // Create extended months array that goes beyond contract end if needed
    const contractStart = DateTime.fromJSDate(project.value.contractStartDate).startOf('month');
    const lastClaimEnd = lastClaim.endDate.endOf('month');
    const contractEnd = DateTime.fromJSDate(project.value.contractEndDate).endOf('month');
    // Use the later of contract end or last claim end
    const chartEnd = lastClaimEnd > contractEnd ? lastClaimEnd : contractEnd;
    let currentMonth = contractStart;

    while(currentMonth <= chartEnd) {
      const key = currentMonth.toLocaleString({ month: '2-digit', year: 'numeric' });

      claimSumPerMonth.set(key, 0);
      currentMonth = currentMonth.plus({ months: 1 });
    }
  }

  claims.value.forEach((claim) => {
    const key = claim.startDate.toLocaleString({ month: '2-digit', year: 'numeric' });

    if(claimSumPerMonth.has(key)) {
      claimSumPerMonth.set(key, claimSumPerMonth.get(key) + claim.totalCertified);
    } else {
      claimSumPerMonth.set(key, claim.totalCertified);
    }
  });

  const cumulativeSum = ((sum) => (value: number) => sum += value)(0);

  return claimSumPerMonth.values().toArray().map(cumulativeSum);
});

// Helper computed to get extended chart months
const extendedChartMonths = computed(() => {
  if(!project.value) {
    return [];
  }

  const lastClaim = claims.value.at(-1);
  const contractStart = DateTime.fromJSDate(project.value.contractStartDate).startOf('month');
  const contractEnd = DateTime.fromJSDate(project.value.contractEndDate).endOf('month');

  if(lastClaim) {
    const lastClaimEnd = lastClaim.endDate.endOf('month');
    const chartEnd = lastClaimEnd > contractEnd ? lastClaimEnd : contractEnd;
    const months = [];
    let currentMonth = contractStart;

    while(currentMonth <= chartEnd) {
      months.push(currentMonth);
      currentMonth = currentMonth.plus({ months: 1 });
    }

    return months;
  }

  return project.value.contractMonths;
});

const baseline = computed(() => {
  if(!project.value) {
    return [];
  }

  // Use extended chart months instead of contract months
  const monthsInProject = extendedChartMonths.value;
  const monthlyTotals = [];

  monthsInProject.forEach((projectMonth, i) => {
    monthlyTotals[i] = 0;

    trades.value.filter((trade) => {
      return trade.baseline_start_date && trade.baseline_end_date;
    }).forEach((trade) => {
      if(trade.baselineStart <= projectMonth && projectMonth <= trade.baselineEnd) {
        monthlyTotals[i] += roundDecimals(
          trade.baseline_forecast[Math.round(Math.abs(trade.baselineStart.diff(projectMonth, ['months']).months))] ??
            0,
        );
      }
    });
  });

  return monthlyTotals;
});

const baselineCumulative = computed(() => {
  return cumulativeSum(baseline.value);
});

const forecast = computed(() => {
  if(!project.value) {
    return [];
  }

  // Use extended chart months instead of contract months
  const monthsInProject = extendedChartMonths.value;
  const monthlyTotals = [];

  monthsInProject.forEach((projectMonth, i) => {
    monthlyTotals[i] = 0;

    trades.value.filter((trade) => {
      return trade.forecast_start_date && trade.forecast_end_date && trade.forecast;
    }).forEach((trade) => {
      if(trade.forecastStart <= projectMonth && projectMonth <= trade.forecastEnd) {
        monthlyTotals[i] += roundDecimals(
          trade.forecast[Math.round(Math.abs(trade.forecastStart.diff(projectMonth, ['months']).months))] ??
            0,
        );
      }
    });
  });

  return monthlyTotals;
});

const forecastCumulative = computed(() => {
  return cumulativeSum(forecast.value);
});

const totalBudget = computed(() => {
  return trades.value.reduce((total, current) => {
    return total + current.totalAdjustedBudget;
  }, 0);
});

// Hover state for pie chart synchronization
const hoveredIndex = ref<number | undefined>(undefined);

// Status badge helper functions
const getStatusBadgeClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-100 text-green-800';

    default:
      return 'bg-red-100 text-red-800';
  }
};

const getStatusBadgeDotClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-400';

    default:
      return 'bg-red-400';
  }
};

const claimMonthToOffset = (dateToCheck: DateTime) => {
  return project.value?.monthsFromContractStart(dateToCheck);
};

// Event handlers for chart hover synchronization
const handleLineChartHover = (index: number | undefined) => {
  hoveredIndex.value = index;
};

// Helper function to determine if a claim row should be dimmed
const isClaimDimmed = (claimIndex: number) => {
  if(hoveredIndex.value === undefined) return false;

  return claimIndex !== hoveredIndex.value;
};
</script>

<style scoped></style>
